import { CircleAlert } from "lucide-react";
import { useState } from "react";

import { Dialog, DialogContent, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import { useExtractPassengers } from "../../api/use-claim-mutations";
import type { ClaimTypeName } from "../../lib/claim-types";
import { SelectClaimType } from "./select-claim-type";

interface ExtractPassengersModalProps {
  claimId: string;
  currentClaimTypeName: ClaimTypeName;
  users: {
    id: string;
    contact: string | null;
    civility: string;
  }[];
  open: boolean;
  onClose: () => void;
}

/** Port of ExtractPassengersModal.vue — 3-step passenger extraction flow. */
export function ExtractPassengersModal({
  claimId,
  currentClaimTypeName,
  users,
  open,
  onClose,
}: ExtractPassengersModalProps) {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [newClaimTypeName, setNewClaimTypeName] = useState<ClaimTypeName>(currentClaimTypeName);
  const extractPassengers = useExtractPassengers(claimId);

  function close() {
    onClose();
    setStep(1);
  }

  function handleExtract() {
    extractPassengers.mutate({
      usersIds: users.map((u) => u.id),
      claimTypeName: newClaimTypeName,
      reason,
    });
    close();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          close();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogPanel className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <CircleAlert className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <DialogTitle className="text-lg font-medium leading-6 text-gray-900">
              Extraire des passagers
            </DialogTitle>
            {step === 1 ? (
              <div className="my-2">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir extraire{" "}
                  {users.length > 1 ? (
                    <span>les passagers selectionnés</span>
                  ) : (
                    <span>le passager selectionné</span>
                  )}{" "}
                  ? Cette action est irréversible.
                </p>
                <div className="mt-6">
                  <ul>
                    {users.map((user) => (
                      <li key={user.id} className="my-3 list-none">
                        <p className="text-sm">
                          {user.civility} <span className="ml-2 text-xs text-gray-500">{user.contact}</span>
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
            {step === 2 ? (
              <div className="my-2">
                <p className="text-sm text-gray-500">Veuillez selectionner le type de la nouvelle claim</p>
                <SelectClaimType
                  usersCount={users.length}
                  value={newClaimTypeName}
                  onSelectionUpdate={setNewClaimTypeName}
                  className="py-4"
                />
              </div>
            ) : null}
            {step === 3 ? (
              <div className="my-2">
                <p className="text-sm text-gray-500">
                  Pour des raisons d'historique, veuillez indiquer la raison qui vous pousse à effectuer cette
                  action
                </p>
                <Textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className="my-4 w-full rounded-md border-2 border-gray-200 p-2 hover:border-gray-300"
                />
              </div>
            ) : null}
          </div>
        </DialogPanel>
        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          {step === 1 || step === 2 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {step === 1 ? "Oui, suivant" : "Valider"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleExtract}
              className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Extraire
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (step === 1) {
                close();
              } else {
                setStep(step - 1);
              }
            }}
            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto sm:text-sm"
          >
            {step === 1 ? "Annuler" : "Retour"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
